import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MentorVerificationRequestsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function MentorVerificationRequestsSubTab() {
  const [requests, setRequests] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approveModalRequestId, setApproveModalRequestId] = useState(null);
  const [approving, setApproving] = useState(false);
  const [revokeModalRequestId, setRevokeModalRequestId] = useState(null);
  const [revoking, setRevoking] = useState(false);
  const [formData, setFormData] = useState({
    mentor_id: '',
    direction_ids: [],
    photoFiles: []
  });

  useEffect(() => {
    fetchProfiles();
    fetchDirections();
  }, []);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mentor-profiles`);
      setProfiles(response.data);
    } catch (err) {
      console.error('Failed to load profiles', err);
    }
  };

  const fetchDirections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mentor-directions`);
      setDirections(response.data);
    } catch (err) {
      console.error('Failed to load directions', err);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/mentor-verification-requests`);
      setRequests(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке заявок: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mentorId = formData.mentor_id ? parseInt(formData.mentor_id, 10) : null;
    if (!mentorId || !formData.direction_ids.length || !formData.photoFiles.length) {
      setError('Укажите ментора, хотя бы одно направление и загрузите хотя бы одно фото.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('mentor_id', mentorId);
      fd.append('direction_ids', JSON.stringify(formData.direction_ids));
      formData.photoFiles.forEach((file) => {
        fd.append('photos', file);
      });
      await axios.post(`${API_URL}/api/mentor-verification-requests/create-with-photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowForm(false);
      setFormData({ mentor_id: '', direction_ids: [], photoFiles: [] });
      fetchRequests();
    } catch (err) {
      setError('Ошибка при создании заявки: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDirectionToggle = (id) => {
    const idNum = parseInt(id, 10);
    setFormData((prev) => ({
      ...prev,
      direction_ids: prev.direction_ids.includes(idNum)
        ? prev.direction_ids.filter((x) => x !== idNum)
        : [...prev.direction_ids, idNum]
    }));
  };

  const handlePhotoChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setFormData((prev) => ({ ...prev, photoFiles: files }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить эту заявку на верификацию?')) {
      try {
        await axios.delete(`${API_URL}/api/mentor-verification-requests/${id}`);
        fetchRequests();
      } catch (err) {
        setError('Ошибка при удалении: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const openApproveModal = (requestId) => {
    setApproveModalRequestId(requestId);
    setError(null);
  };

  const closeApproveModal = () => {
    setApproveModalRequestId(null);
  };

  const handleApproveConfirm = async () => {
    if (!approveModalRequestId) return;
    setApproving(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/mentor-verification-requests/${approveModalRequestId}/approve`);
      closeApproveModal();
      fetchRequests();
    } catch (err) {
      setError('Ошибка при одобрении заявки: ' + (err.response?.data?.detail || err.message));
    } finally {
      setApproving(false);
    }
  };

  const openRevokeModal = (requestId) => {
    setRevokeModalRequestId(requestId);
    setError(null);
  };

  const closeRevokeModal = () => {
    setRevokeModalRequestId(null);
  };

  const handleRevokeConfirm = async () => {
    if (!revokeModalRequestId) return;
    setRevoking(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/mentor-verification-requests/${revokeModalRequestId}/revoke`);
      closeRevokeModal();
      fetchRequests();
    } catch (err) {
      setError('Ошибка при отзыве верификации: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRevoking(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ mentor_id: '', direction_ids: [], photoFiles: [] });
    setError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const mentorLabel = (mentorId) => {
    const p = profiles.find((x) => x.id === mentorId);
    if (!p) return `ID ${mentorId}`;
    return `${p.first_name} ${p.last_name || ''} (${p.login})`.trim();
  };

  const directionLabel = (id) => {
    const d = directions.find((x) => x.id === id);
    return d ? d.name : `ID ${id}`;
  };

  if (loading && requests.length === 0) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="mentor-verification-requests-sub-tab">
      <div className="tab-header">
        <h2>Заявки на верификацию</h2>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Создать заявку
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h3>Новая заявка на верификацию</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="req_mentor">Ментор *</label>
              <select
                id="req_mentor"
                value={formData.mentor_id}
                onChange={(e) => setFormData({ ...formData, mentor_id: e.target.value })}
                required
              >
                <option value="">Выберите ментора</option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.first_name} {p.last_name || ''} — {p.login}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Направления * (выберите одно или несколько)</label>
              <div className="direction-checkboxes">
                {directions.length === 0 ? (
                  <p className="hint">Сначала добавьте направления во вкладке «Направления менторства».</p>
                ) : (
                  directions.map((d) => (
                    <label key={d.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.direction_ids.includes(d.id)}
                        onChange={() => handleDirectionToggle(d.id)}
                      />
                      <span>{d.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="req_photos">Фотографии * (JPEG, PNG, WebP, до 5 MB каждая)</label>
              <input
                type="file"
                id="req_photos"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handlePhotoChange}
              />
              {formData.photoFiles.length > 0 && (
                <span className="file-hint">Выбрано файлов: {formData.photoFiles.length}</span>
              )}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Отправка...' : 'Создать заявку'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {approveModalRequestId !== null && (
        <div className="modal-overlay" onClick={closeApproveModal}>
          <div className="modal-content approve-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Подтверждение одобрения заявки на верификацию</h3>
            <p>Вы уверены, что хотите одобрить заявку на верификацию №{approveModalRequestId}? После одобрения для данной заявки будет создана запись об одобрении.</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApproveConfirm}
                disabled={approving}
              >
                {approving ? 'Обработка...' : 'Одобрить верификацию'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeApproveModal}
                disabled={approving}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeModalRequestId !== null && (
        <div className="modal-overlay" onClick={closeRevokeModal}>
          <div className="modal-content revoke-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Отзыв верификации</h3>
            <p>Вы уверены, что хотите отозвать верификацию по заявке №{revokeModalRequestId}? Статус заявки будет изменён на «На рассмотрении».</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-revoke"
                onClick={handleRevokeConfirm}
                disabled={revoking}
              >
                {revoking ? 'Обработка...' : 'Отозвать верификацию'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeRevokeModal}
                disabled={revoking}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Ментор</th>
              <th>Дата создания</th>
              <th>ID фото</th>
              <th>Направления</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет заявок на верификацию. Создайте первую заявку (кнопка выше).
                </td>
              </tr>
            ) : (
              requests.map((req) => (
                <tr key={req.id}>
                  <td>{req.id}</td>
                  <td>{mentorLabel(req.mentor_id)}</td>
                  <td>{formatDate(req.created_at)}</td>
                  <td>{req.photo_ids && req.photo_ids.length ? req.photo_ids.join(', ') : '—'}</td>
                  <td>{req.direction_ids && req.direction_ids.length ? req.direction_ids.map(directionLabel).join(', ') : '—'}</td>
                  <td>
                    {req.is_approved ? (
                      <span className="status-badge status-approved">Одобрено</span>
                    ) : (
                      <span className="status-badge status-pending">На рассмотрении</span>
                    )}
                  </td>
                  <td>
                    {!req.is_approved && (
                      <button
                        className="btn btn-approve"
                        onClick={() => openApproveModal(req.id)}
                        title="Одобрить заявку"
                      >
                        Одобрить
                      </button>
                    )}
                    {req.is_approved && (
                      <button
                        className="btn btn-revoke"
                        onClick={() => openRevokeModal(req.id)}
                        title="Отозвать верификацию"
                      >
                        Отозвать верификацию
                      </button>
                    )}
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(req.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MentorVerificationRequestsSubTab;
